import React, {useEffect, useRef, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Menu} from "primereact/menu";
import {ProgressSpinner} from "primereact/progressspinner";
import {useDispatch} from "react-redux";
import Icon from "../../../system/components/icon";
import {setMessage} from "../../../system/app-slice";
import {CLOSED_GOAL_STATUSES, GOAL_STATUS, goalUpdateApi} from "../../../models/goal-update/goal-update-api";
import "./goal-updates.css";

// «تعیین تکلیف اهداف پایان بازه زمانی» (docs/tasks/17-goal-updates.md): open goals whose period has ended; move each to
// a next period or close it. Opened from the period-end notification (?goalPeriodReview=1) and the goals page banner.

// the goals page banner and the modal opened from a notification are separate; decisions tell every banner to reload
const REVIEW_CHANGED = "zofino:goal-period-review-changed";
const announceChange = () => window.dispatchEvent(new Event(REVIEW_CHANGED));

function ReviewGoal({goal, decision, onDecide}) {
    const moveMenu = useRef(null);
    const closeMenu = useRef(null);
    const progress = Math.max(0, Math.min(100, Number(goal.progress) || 0));
    const moveLabel = decision?.action === "move" ? decision.label : "انتقال به بازه بعدی";
    const closeLabel = decision?.action === "close" ? `بستن: ${GOAL_STATUS[decision.status]?.label}` : "بستن هدف";

    return (
        <div className="gu-review__goal flex flex-column gap-3">
            <div className="flex align-items-start justify-content-between gap-3">
                <div className="flex flex-column gap-2 min-w-0">
                    <strong className="text-lg">{goal.title}</strong>
                    <span className="text-xs gu-muted">{[goal.team?.name, goal.period?.label].filter(Boolean).join(" · ")}</span>
                </div>
                <span className="font-bold" style={{color: "#a93100"}}>{progress.toLocaleString("fa-IR")}٪</span>
            </div>
            <div className="gu-review__bar"><span style={{width: `${progress}%`}}/></div>
            <div className="flex flex-column sm:flex-row gap-3">
                <Menu
                    ref={moveMenu}
                    popup
                    className="rtl text-sm"
                    model={[{
                        label: "انتخاب بازه زمانی جدید",
                        items: (goal.nextPeriods || []).map((period) => ({
                            label: period.label,
                            command: () => onDecide({action: "move", period: {range: period.range, year: period.year}, label: period.label}),
                        })),
                    }]}
                />
                <Menu
                    ref={closeMenu}
                    popup
                    className="rtl text-sm"
                    model={[{
                        label: "وضعیت پایانی هدف",
                        items: CLOSED_GOAL_STATUSES.map((status) => ({
                            label: GOAL_STATUS[status].label,
                            command: () => onDecide({action: "close", status}),
                        })),
                    }]}
                />
                <button type="button" className={`gu-review__action ${decision?.action === "move" ? "is-active" : ""}`} onClick={(event) => moveMenu.current?.toggle(event)}>
                    <Icon icon="mi mi:redo" className="text-lg"/>{moveLabel}<Icon icon="mi mi:expand_more" className="text-lg"/>
                </button>
                <button type="button" className={`gu-review__action ${decision?.action === "close" ? "is-active" : ""}`} onClick={(event) => closeMenu.current?.toggle(event)}>
                    <Icon icon="mi mi:check_circle" className="text-lg"/>{closeLabel}
                </button>
            </div>
        </div>
    );
}

function GoalPeriodReviewDialog({visible, onHide, onDone}) {
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [decisions, setDecisions] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setDecisions({});
        setData(null);
        goalUpdateApi.periodReview()
            .then(setData)
            .catch(() => setData({goals: [], period: null, failed: true}));
    }, [visible]);

    const goals = data?.goals || [];
    const decided = goals.filter((goal) => decisions[goal.documentId]);

    const submit = async () => {
        if (!decided.length || saving) return;
        setSaving(true);
        try {
            await goalUpdateApi.decidePeriodReview(decided.map((goal) => ({goal: goal.documentId, ...decisions[goal.documentId], label: undefined})));
            dispatch(setMessage({severity: "success", content: `تکلیف ${decided.length.toLocaleString("fa-IR")} هدف مشخص شد.`}));
            announceChange();
            onDone?.();
            onHide?.();
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "ثبت انجام نشد. دوباره تلاش کنید."}));
        } finally {
            setSaving(false);
        }
    };

    const later = async () => {
        try {
            await goalUpdateApi.later();
            dispatch(setMessage({severity: "info", content: "سه روز دیگر دوباره یادآوری می‌کنیم."}));
            announceChange();
        } catch (error) {
            // closing is fine even if the snooze could not be saved
        }
        onHide?.();
    };

    return (
        <Dialog visible={visible} onHide={onHide} showHeader={false} className="gu-review rtl" dismissableMask draggable={false} blockScroll>
            <div className="gu shabnam-font" dir="rtl">
                <div className="p-5 pb-3 flex flex-column gap-2">
                    <div className="flex align-items-start justify-content-between gap-3">
                        <h2 className="m-0 text-2xl font-bold">تعیین تکلیف اهداف پایان بازه زمانی</h2>
                        {data?.period?.label && <span className="gu-chip" style={{background: "#fde7df", color: "#a93100"}}>{data.period.label}</span>}
                    </div>
                    <p className="m-0 text-sm gu-muted">
                        {data?.period?.label ? `بازه زمانی ${data.period.label} به پایان رسیده است. لطفاً وضعیت اهداف زیر را مشخص کنید.` : "لطفاً وضعیت اهداف زیر را مشخص کنید."}
                    </p>
                </div>
                <div className="px-5 pb-4 flex flex-column gap-3" style={{maxHeight: "60vh", overflowY: "auto"}}>
                    {!data && <div className="flex justify-content-center p-5"><ProgressSpinner style={{width: "2.5rem", height: "2.5rem"}} strokeWidth="4"/></div>}
                    {data && !goals.length && (
                        <div className="p-5 text-center text-sm gu-muted">{data.failed ? "دریافت اهداف انجام نشد." : "هدفی برای تعیین تکلیف باقی نمانده است."}</div>
                    )}
                    {goals.map((goal) => (
                        <ReviewGoal
                            key={goal.documentId}
                            goal={goal}
                            decision={decisions[goal.documentId]}
                            onDecide={(decision) => setDecisions((current) => ({...current, [goal.documentId]: decision}))}
                        />
                    ))}
                </div>
                <div className="gu-review__footer">
                    <button type="button" className="gu-soft-button gu-soft-button--plain" onClick={later}>بعداً انجام می‌دهم</button>
                    <button type="button" className="gu-review__submit" disabled={!decided.length || saving} onClick={submit}>
                        {saving ? "در حال ثبت…" : "تایید و ثبت نهایی"}
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

// goals page: a banner while the member owns open goals whose period has ended
export function GoalPeriodReviewBanner() {
    const [count, setCount] = useState(0);
    const [label, setLabel] = useState("");
    const [visible, setVisible] = useState(false);

    const load = () => goalUpdateApi.periodReview()
        .then((result) => {
            setCount(result?.goals?.length || 0);
            setLabel(result?.period?.label || "");
        })
        .catch(() => setCount(0));

    useEffect(() => {
        load();
        window.addEventListener(REVIEW_CHANGED, load);
        return () => window.removeEventListener(REVIEW_CHANGED, load);
        // load when the goals page opens and after every decision
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!count) return null;
    return (
        <div className="gu px-3 pb-2" dir="rtl">
            <div className="gu-banner">
                <span className="flex align-items-center gap-2 text-sm">
                    <span className="inline-flex flex-none" style={{color: "#a93100"}}><Icon icon="mi mi:event_busy" className="text-xl"/></span>
                    <strong>{count === 1 ? "بازه زمانی یکی از اهداف شما تمام شده است" : `بازه زمانی ${count.toLocaleString("fa-IR")} هدف شما تمام شده است`}</strong>
                    <span className="gu-muted text-xs">{label ? `(${label})` : ""} مشخص کنید به بازه بعد منتقل شوند یا بسته شوند.</span>
                </span>
                <button type="button" className="gu-review__submit" style={{padding: ".55rem 1.4rem", fontSize: ".8rem"}} onClick={() => setVisible(true)}>تعیین تکلیف</button>
            </div>
            <GoalPeriodReviewDialog visible={visible} onHide={() => { setVisible(false); load(); }} onDone={load}/>
        </div>
    );
}

export default GoalPeriodReviewDialog;
